/**
 * Signup 18+ attestation flag (Google-OAuth path).
 *
 * Google OAuth carries no date of birth, so a fresh Google signup would
 * otherwise land on the post-signup DobGate popup. When a user initiates Google
 * signup FROM THE SIGNUP PAGE, the affirmation microcopy shown at the point of
 * the signup action ("By continuing, you confirm you are 18 or older...") IS the
 * informed, affirmative 18+ consent. We persist a one-shot flag in
 * sessionStorage BEFORE the OAuth redirect; on OAuth return AuthContext reads
 * (and clears) it and passes over18_attested:true to the registration trigger,
 * so age_verified is stamped inline and the DobGate popup is bypassed.
 *
 * sessionStorage (not localStorage) is deliberate: it is per-tab and survives
 * the full-page OAuth redirect to Google and back, but does not leak into other
 * tabs or persist beyond the tab session. The flag is consumed exactly once.
 *
 * IMPORTANT: this flag is only ever set from the SIGN UP surface, never the
 * sign-in surface, so an existing user signing in is never treated as a fresh
 * attestation. Hard age is still verified by ID at KYC before any redemption.
 */
import logger from "./logger";

export const SIGNUP_ATTESTATION_KEY = "cluck.signup.over18Attested.v1";

/** Persist the 18+ attestation flag just before the Google OAuth redirect. */
export function setSignupAttestationFlag() {
  try {
    sessionStorage.setItem(SIGNUP_ATTESTATION_KEY, "true");
  } catch (e) {
    // sessionStorage can be blocked (private mode / strict browsers). The
    // DobGate fallback still covers this account, so this is non-fatal.
    logger.warn("Failed to set signup attestation flag", {
      error: e?.message,
    });
  }
}

/**
 * Read AND clear the attestation flag in one shot. Returns true only when the
 * signup page set it before this OAuth return. Always clears the key so it can
 * never leak into a later sign-in on the same tab.
 */
export function consumeSignupAttestationFlag() {
  try {
    const val = sessionStorage.getItem(SIGNUP_ATTESTATION_KEY);
    if (val !== null) sessionStorage.removeItem(SIGNUP_ATTESTATION_KEY);
    return val === "true";
  } catch (e) {
    logger.warn("Failed to read signup attestation flag", {
      error: e?.message,
    });
    return false;
  }
}
